// Shared factory for per-table user column preferences (visibility + order),
// persisted to localStorage per user's browser. Same pattern used by
// AssetManagement, VLAN IP List, Disposal History.
//
// Usage:
//   import { makeColumnPrefs } from '@/lib/tableColumnPrefs';
//   export const ipListCols = makeColumnPrefs([
//     { key: 'ip_address', label: 'IPアドレス', defaultOn: true, fixed: true },
//     ...
//   ], 'it_iplist_cols_v1');
//
// The returned object exposes: DEFS, FIXED_KEYS, defaultConfig(), load(),
// save(cfg), normalize(saved). `TableColumnsDialog` and `useTableColumns()`
// consume the same shape.

export function makeColumnPrefs(DEFS, LS_KEY) {
  const FIXED = new Set(DEFS.filter((d) => d.fixed).map((d) => d.key));
  const KNOWN = new Set(DEFS.map((d) => d.key));

  const defaultConfig = () => ({ columns: DEFS.map((d) => ({ key: d.key, visible: d.defaultOn })) });

  // Merge saved config with current defs so newly-added columns auto-appear
  // and removed columns drop out; keeps the user's order + visibility.
  const normalize = (saved) => {
    const base = defaultConfig();
    if (!saved || typeof saved !== 'object') return base;
    const savedCols = Array.isArray(saved.columns) ? saved.columns.filter((c) => c && KNOWN.has(c.key)) : [];
    const present = new Set(savedCols.map((c) => c.key));
    DEFS.forEach((d) => { if (!present.has(d.key)) savedCols.push({ key: d.key, visible: d.defaultOn }); });
    savedCols.forEach((c) => { if (FIXED.has(c.key)) c.visible = true; });
    return { columns: savedCols };
  };

  const load = () => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) return normalize(JSON.parse(raw));
    } catch { /* ignore */ }
    return defaultConfig();
  };

  const save = (cfg) => { try { localStorage.setItem(LS_KEY, JSON.stringify(cfg)); } catch { /* ignore */ } };

  return { defaultConfig, normalize, load, save, DEFS, FIXED_KEYS: FIXED };
}
